// src/components/ProgramList.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api'; // Usamos nuestra instancia de Axios
import useDebounce from '../hooks/useDebounce'; // Importamos el hook
import { IoAddCircle, IoSearch } from "react-icons/io5";
import Spinner from './Spinner'; // Importamos el Spinner para el estado de carga

const ProgramList = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // --- INICIO DE LOS NUEVOS ESTADOS PARA LA BÚSQUEDA ---
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // Espera 500ms después de que el usuario deja de escribir
  // --- FIN DE LOS NUEVOS ESTADOS ---

  // --- USEEFFECT MEJORADO ---
  // Usamos useCallback para memorizar la función y evitar que se recree en cada render
  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Preparamos los parámetros para la petición.
      // Si debouncedSearchTerm está vacío, el parámetro 'search' no se enviará.
      const params = {
        search: debouncedSearchTerm || undefined,
      };

      const response = await api.get('/programs', { params });
      setPrograms(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar los programas.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]); // La función solo se recreará si el término de búsqueda "debounced" cambia

  // Este useEffect se ejecuta al montar el componente y cada vez que fetchPrograms cambia
  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);
  // --- FIN DEL USEEFFECT MEJORADO ---

  // El renderizado condicional de loading/error no cambia
  if (loading && programs.length === 0) return <Spinner />;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="w-full h-full container mx-auto p-4 md:p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-white">
          Mis Programas de Entrenamiento
        </h1>
        <Link 
          to="/programs/create" 
          className="flex items-center bg-marizan-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
        >
          <IoAddCircle size={22} className="mr-2" />
          Crear Programa
        </Link>
      </div>

      {/* --- INICIO DEL NUEVO INPUT DE BÚSQUEDA --- */}
      <div className="mb-6 relative">
        <input
          type="text"
          placeholder="Buscar por nombre o descripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-lg p-3 pl-10 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-marizan-red focus:border-marizan-red"
        />
        <IoSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>
      {/* --- FIN DEL NUEVO INPUT DE BÚSQUEDA --- */}

      {/* La lógica de renderizado de la lista ahora incluye un estado de carga "en línea" */}
      {loading && <p className="text-center text-gray-400">Buscando...</p>}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.length > 0 ? (
            programs.map(program => (
              <Link key={program.id} to={`/programs/${program.id}`}>
                <div className="bg-black bg-opacity-40 p-6 rounded-lg shadow-xl border border-gray-700 hover:border-marizan-red hover:bg-opacity-50 transition-all duration-300 h-full cursor-pointer">
                  <h2 className="text-2xl font-bold text-white mb-2">{program.name}</h2>
                  <p className="text-gray-400 h-24 overflow-y-auto">{program.description || 'Sin descripción.'}</p>
                </div>
              </Link>
            ))
          ) : (
            <p className="text-gray-400 col-span-full text-center py-10">
              {debouncedSearchTerm 
                ? `No se encontraron programas que coincidan con "${debouncedSearchTerm}".`
                : "No has creado ningún programa de entrenamiento todavía. ¡Crea el primero!"
              }
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramList;